import { useMemo } from 'react';

import { CacheKey } from '../types/cache-keys';
import { useFetchAllDeliveryTargets } from './campaign-hooks';
import { useSfRecordsById } from './useSfRecordsById';
import { getSfRecord } from '../support/salesforceBatch';
import type { CampaignDeliveryTarget } from '../support/campaigns';
import type { Opportunity } from '../types/opportunity';

/**
 * Pipeline-$ source data for the Leader "Campaigns" tab.
 *
 * A campaign delivery reaches its Salesforce opportunity through its targets:
 * `CampaignDelivery` → `CampaignDeliveryTarget.delivery` → `target.opportunity` (= Salesforce
 * OpportunityId). The opportunity `Amount` is authoritative on Salesforce, not mirrored locally, so we
 * batch-fetch every referenced opportunity through the raw SOQL passthrough (`useSfRecordsById` →
 * `WHERE Id IN (...)`) rather than one scalar `sf/opportunity/<id>` request each — the SF proxy
 * silently drops a structured `Id` anyOf filter ([[salesforce-id-list-filters-are-dropped]]), but the
 * IN list is fully supported and collapses hundreds of round-trips into one. The pure rollup in
 * `support/campaignLeader.ts` then buckets the summed amounts by delivery status over an org subtree.
 */
export interface CampaignPipelineData {
  /** delivery id → its targets (for delivery → opportunity resolution). */
  targetsByDelivery: Map<number, CampaignDeliveryTarget[]>;
  /** Salesforce OpportunityId → its Amount in dollars (0 when blank/unknown). */
  amountByOpp: Map<string, number>;
  isLoading: boolean;
}

export function useCampaignPipeline(options: { enabled?: boolean } = {}): CampaignPipelineData {
  const enabled = options.enabled ?? true;
  const { data: targets, isLoading: loadingTargets } = useFetchAllDeliveryTargets({ enabled });

  const targetsByDelivery = useMemo(() => {
    const map = new Map<number, CampaignDeliveryTarget[]>();
    for (const t of (targets ?? []) as CampaignDeliveryTarget[]) {
      const list = map.get(t.delivery) ?? [];
      list.push(t);
      map.set(t.delivery, list);
    }
    return map;
  }, [targets]);

  // The distinct opportunity ids any target links to — the fan-out set.
  const oppIds = useMemo(() => {
    const set = new Set<string>();
    for (const t of (targets ?? []) as CampaignDeliveryTarget[]) {
      if (t.opportunity) set.add(t.opportunity);
    }
    return Array.from(set);
  }, [targets]);

  const { byId: oppById, loading: loadingOpps } = useSfRecordsById<Opportunity>(
    'Opportunity',
    ['Amount'],
    oppIds,
    { cacheKey: String(CacheKey.Opportunity), enabled },
  );

  const amountByOpp = useMemo(() => {
    const map = new Map<string, number>();
    for (const id of oppIds) {
      const opp = getSfRecord(oppById, id);
      // `Opportunity.Amount` is typed string|null on the SF mirror — always coerce.
      const num = opp?.Amount == null ? 0 : Number(opp.Amount);
      map.set(id, Number.isFinite(num) ? num : 0);
    }
    return map;
  }, [oppIds, oppById]);

  return { targetsByDelivery, amountByOpp, isLoading: loadingTargets || loadingOpps };
}

export default useCampaignPipeline;
